import React, { useEffect, useRef } from 'react';
import { experienceData } from '../utils/constants';
import { motion } from 'framer-motion';
import { BriefcaseIcon, MapPinIcon, CalendarIcon, ExternalLinkIcon, ArrowRight } from 'lucide-react';
const Experience = ({
  language = 'en'
}) => {
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll('.fade-in, .slide-in');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const translations = {
    en: {
      professional: 'Professional',
      experience: 'Experience',
      visit: 'Visit',
      current: 'Current',
      incoming: 'Incoming'
    },
    vi: {
      professional: 'Kinh Nghiệm',
      experience: 'Chuyên Môn',
      visit: 'Truy cập',
      current: 'Hiện tại',
      incoming: 'Sắp tới'
    }
  };
  const t = translations[language];
  return <section id="experience" ref={sectionRef} className="w-full py-24 bg-secondary" data-id="element-555">
      <div className="container mx-auto px-4" data-id="element-556">
        <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4 fade-in text-white" data-id="element-557">
          {t.professional} <span className="gradient-text" data-id="element-558">{t.experience}</span>
        </h2>
        <p className="text-center text-gray-300 mb-16 fade-in" data-id="element-559">
          {language === 'en' ? 'A journey through finance, technology, and leadership' : 'Hành trình qua tài chính, công nghệ và lãnh đạo'}
        </p>

        {/* Timeline Container */}
        <div className="relative max-w-6xl mx-auto" data-id="element-560">
          {/* Vertical Line - Desktop */}
          <div className="absolute hidden lg:block left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent-cyan via-accent-violet to-accent-amber" data-id="element-561"></div>

          {/* Timeline Items */}
          <div className="space-y-8 lg:space-y-12" data-id="element-562">
            {experienceData.map((experience, index) => {
            const isEven = index % 2 === 0;
            const isCurrent = experience.dates.includes('Present') || experience.dates.includes('Hiện tại');
            const isIncoming = experience.role.includes('Incoming') || experience.roleVi.includes('Sắp tới');
            return <div key={index} className={`relative flex flex-col lg:flex-row ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 fade-in`} style={{
              transitionDelay: `${index * 100}ms`
            }} data-id="element-563">
                  {/* Timeline Dot - Desktop */}
                  <div className="absolute hidden lg:flex left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-br from-accent-cyan to-accent-violet shadow-lg z-10 items-center justify-center" data-id="element-564">
                    <div className="w-2 h-2 rounded-full bg-white" data-id="element-565"></div>
                  </div>

                  {/* Content Card */}
                  <div className={`w-full lg:w-[calc(50%-2rem)] ${isEven ? 'lg:pr-8' : 'lg:pl-8'}`} data-id="element-566">
                    <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className="group relative bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-700 hover:border-accent-cyan/30" data-id="element-567">
                      {/* Status Badge */}
                      {(isCurrent || isIncoming) && <div className="absolute -top-3 -right-3 z-10" data-id="element-568">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-lg ${isCurrent ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'}`} data-id="element-569">
                            {isCurrent ? t.current : t.incoming}
                          </span>
                        </div>}

                      {/* Header */}
                      <div className="flex items-start gap-4 mb-4" data-id="element-570">
                        <div className="w-16 h-16 rounded-xl bg-white p-2 flex items-center justify-center shadow-md flex-shrink-0" data-id="element-571">
                          <img src={experience.logo} alt={experience.company} className="max-w-full max-h-full object-contain" data-id="element-572" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-573">
                          <h3 className="text-xl font-bold text-white mb-1 line-clamp-2" data-id="element-574">
                            {language === 'en' ? experience.role : experience.roleVi}
                          </h3>
                          <p className="text-accent-cyan font-semibold mb-2" data-id="element-575">
                            {experience.company}
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-300" data-id="element-576">
                            <div className="flex items-center gap-1.5" data-id="element-577">
                              <MapPinIcon size={14} className="text-accent-violet flex-shrink-0" data-id="element-578" />
                              <span data-id="element-579">
                                {language === 'en' ? experience.location : experience.locationVi}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-580">
                              <CalendarIcon size={14} className="text-accent-amber flex-shrink-0" data-id="element-581" />
                              <span data-id="element-582">
                                {language === 'en' ? experience.dates : experience.datesVi}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <ul className="space-y-2.5 mb-4" data-id="element-583">
                        {(language === 'en' ? experience.description : experience.descriptionVi).map((item, idx) => <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-200" data-id="element-584">
                            <ArrowRight size={16} className="text-accent-cyan mt-0.5 flex-shrink-0" data-id="element-585" />
                            <span className="leading-relaxed" data-id="element-586">{item}</span>
                          </li>)}
                      </ul>

                      {/* Website Link */}
                      {experience.website && <a href={experience.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-accent-cyan hover:text-accent-violet transition-colors group/link" data-id="element-587">
                          <ExternalLinkIcon size={16} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" data-id="element-588" />
                          <span data-id="element-589">
                            {t.visit} {experience.company}
                          </span>
                        </a>}

                      {/* Hover Effect Border */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent-cyan/0 via-accent-violet/0 to-accent-amber/0 group-hover:from-accent-cyan/5 group-hover:via-accent-violet/5 group-hover:to-accent-amber/5 transition-all duration-300 pointer-events-none" data-id="element-590"></div>
                    </motion.div>
                  </div>

                  {/* Spacer for alignment on desktop */}
                  <div className="hidden lg:block w-[calc(50%-2rem)]" data-id="element-591"></div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
};
export default Experience;